/**
 * The opening narration — the one piece of text the GM reads aloud verbatim.
 *
 * Everything else the showrunner produces is prep the GM translates into their
 * own words. This is the deliberate exception, written once at session start
 * to get the table into the scene. Hence the copy button and the generous type
 * size: it is meant to be read off the screen, out loud, at a table.
 */
import { useState } from 'react';

function narrationParagraphs(text: string): string[][] {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });
  return text
    .trim()
    .split(/\n+/)
    .map((paragraph) =>
      Array.from(segmenter.segment(paragraph), ({ segment }) => segment.trim()).filter(
        Boolean,
      ),
    )
    .filter((paragraph) => paragraph.length > 0);
}

export function NarrationCard({ text, presentationCollapsed }: { text: string; presentationCollapsed?: boolean | number }) {
  const [copied, setCopied] = useState(false);
  const [localCollapsed, setCollapsed] = useState(false);
  const progress = typeof presentationCollapsed === 'number' ? presentationCollapsed : null;
  const collapsed = progress !== null ? progress > 0.5 : (presentationCollapsed ?? localCollapsed);
  const paragraphs = narrationParagraphs(text);
  let beatIndex = 0;

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the text is on screen regardless.
    }
  }

  return (
    <section className="narration">
      <header className="narration__head">
        <h2 className="narration__title">Read this aloud to open</h2>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button type="button" className="button button--quiet" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
          >
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </header>
      {collapsed && progress === null ? null : (
        <div style={progress === null ? undefined : { display: 'grid', gridTemplateRows: `${1 - progress}fr`, opacity: 1 - progress }}>
        <div style={{ minHeight: 0, overflow: 'hidden' }}>
        <div className="narration__body">
          {paragraphs.map((paragraph, paragraphIndex) => (
            <div className="narration__paragraph" key={paragraphIndex}>
              {paragraph.map((sentence, sentenceIndex) => {
                const arrival = beatIndex++;
                return (
                  <p
                    className="narration__beat narration__beat--arrival"
                    key={sentenceIndex}
                    style={{ animationDelay: `${Math.min(arrival * 90, 540)}ms` }}
                  >
                    {sentence}
                  </p>
                );
              })}
            </div>
          ))}
        </div>
        </div>
        </div>
      )}
    </section>
  );
}
